import { Button } from "@/components/flexkit/Button";
import { Combobox } from "@/components/flexkit/combobox";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import {
    useAuthenticatedRunnerIds,
    useListRunners,
    useListSCMOrganizationsAcrossRunners,
    useSearchRepositoriesAcrossRunners,
} from "@/queries/runner-queries";
import type { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { SearchMode, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { TrackLocations } from "@/hooks/use-segment";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import {
    extractRepoPath,
    getScmHost,
    getScopeConfig,
    getWebhookSettingsUrl,
    scmRepoToScope,
    scopeToDisplayString,
    WEBHOOK_PROVIDER_CONFIG,
    type SCMRepository,
} from "@/components/webhooks/webhook-config";
import { IconClose } from "@/assets/icons/geist/IconClose";

export type WebhookScopeEntry = {
    host: string;
    owner: string;
    name: string;
};

export type WebhookOrgScope = {
    host: string;
    name: string;
};

type WebhookScopeFieldProps = {
    provider: WebhookProvider;
    webhookType: WebhookType;
    /** Legacy single-scope value, used for org type and backward compatibility */
    value: string;
    onChange: (value: string) => void;
    /** Multi-scope entries for repository type */
    scopes?: WebhookScopeEntry[];
    onScopesChange?: (scopes: WebhookScopeEntry[]) => void;
    /** Structured organization scope */
    organizationScope?: WebhookOrgScope;
    onOrganizationScopeChange?: (scope: WebhookOrgScope | undefined) => void;
    disabled?: boolean;
    readOnly?: boolean;
    /** If true, the settings URL will point to the "new webhook" page */
    isNewWebhook?: boolean;
};

export const WebhookScopeField: FC<WebhookScopeFieldProps> = ({
    provider,
    webhookType,
    value,
    onChange,
    scopes = [],
    onScopesChange,
    onOrganizationScopeChange,
    disabled = false,
    readOnly = false,
    isNewWebhook = false,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 400, { trailing: true });
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

    const scmHost = getScmHost(provider);
    const providerName = WEBHOOK_PROVIDER_CONFIG[provider].name;
    const { isOrgType, scopeLabel, comboboxPlaceholder } = getScopeConfig(webhookType);
    const settingsUrl = getWebhookSettingsUrl(
        provider,
        webhookType,
        value || (scopes[0] ? scopeToDisplayString(scopes[0]) : ""),
        isNewWebhook,
    );

    // Only load data if not read-only
    const shouldLoadData = !readOnly;

    // Get runners for querying: active, non-local runners
    const { runners: allRunners } = useListRunners({ enabled: shouldLoadData });
    const runners = useMemo(
        () => allRunners.filter((r) => r.status?.phase === RunnerPhase.ACTIVE && r.kind !== RunnerKind.LOCAL),
        [allRunners],
    );
    const runnerIds = useMemo(() => runners.map((r) => r.runnerId), [runners]);

    // Pre-filter to only runners authenticated for this SCM host
    const { authenticatedRunnerIds } = useAuthenticatedRunnerIds({
        runnerIds,
        scmHost,
        enabled: shouldLoadData && runnerIds.length > 0 && !!scmHost,
    });

    // Fetch organizations from authenticated runners only
    const { organizations, isFetching: isFetchingOrgs } = useListSCMOrganizationsAcrossRunners({
        runnerIds: authenticatedRunnerIds,
        scmHost,
        enabled: shouldLoadData && isOrgType && authenticatedRunnerIds.length > 0 && !!scmHost,
    });

    // Fetch repositories from authenticated runners only
    const { repositories: repoResults, isLoading: isLoadingRepos } = useSearchRepositoriesAcrossRunners({
        runnerIds: authenticatedRunnerIds,
        query: debouncedSearchValue,
        scmHost,
        searchMode: SearchMode.KEYWORD,
        enabled: shouldLoadData && !isOrgType && authenticatedRunnerIds.length > 0 && !!scmHost,
        pageSize: 50,
    });

    const repositories = useMemo(
        (): SCMRepository[] =>
            repoResults.map((repo) => ({
                name: repo.name,
                url: repo.url,
            })),
        [repoResults],
    );

    // Filter out already-selected repositories
    const availableRepositories = useMemo(() => {
        const selectedPaths = new Set(scopes.map((s) => scopeToDisplayString(s)));
        return repositories.filter((repo) => !selectedPaths.has(extractRepoPath(repo.url)));
    }, [repositories, scopes]);

    // Only update search value while the popover is open.
    const handleSearchValueChanged = useCallback(
        (newValue: string) => {
            if (isPopoverOpen) {
                setSearchValue(newValue);
            }
        },
        [isPopoverOpen],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        setIsPopoverOpen(open);
        if (!open) {
            setSearchValue("");
        }
    }, []);

    const handleRepoSelected = useCallback(
        (repoPath: string) => {
            const repo = repositories.find((r) => extractRepoPath(r.url) === repoPath);
            if (!repo || !onScopesChange) return;

            const scopeEntry = scmRepoToScope(repo, provider);
            const alreadyExists = scopes.some((s) => s.owner === scopeEntry.owner && s.name === scopeEntry.name);
            if (!alreadyExists) {
                onScopesChange([...scopes, scopeEntry]);
            }
            // Also update legacy value for backward compatibility
            if (!value) {
                onChange(repoPath);
            }
        },
        [repositories, scopes, onScopesChange, provider, value, onChange],
    );

    const handleRemoveScopeClick = useCallback((index: number) => {
        setPendingRemoveIndex(index);
    }, []);

    const handleConfirmRemoveScope = useCallback(() => {
        if (pendingRemoveIndex === null || !onScopesChange) return;
        const newScopes = scopes.filter((_, i) => i !== pendingRemoveIndex);
        onScopesChange(newScopes);
        if (newScopes.length > 0) {
            onChange(scopeToDisplayString(newScopes[0]));
        } else {
            onChange("");
        }
        setPendingRemoveIndex(null);
    }, [pendingRemoveIndex, scopes, onScopesChange, onChange]);

    const handleCancelRemoveScope = useCallback(() => {
        setPendingRemoveIndex(null);
    }, []);

    const handleOpenSettings = () => {
        if (settingsUrl) {
            window.open(settingsUrl, "_blank", "noopener,noreferrer");
        }
    };

    // For org type, keep the existing single-select behavior
    if (isOrgType) {
        return (
            <div className="flex flex-col gap-2">
                <Label htmlFor="webhook-scope" className="text-sm font-medium">
                    {scopeLabel}
                </Label>
                <div className="flex items-center gap-1">
                    {readOnly ? (
                        <Input
                            id="webhook-scope"
                            value={value}
                            readOnly
                            className="flex-1"
                            data-testid="webhook-scope-input"
                        />
                    ) : (
                        <Combobox
                            value={value}
                            onValueChange={(val) => {
                                onChange(val);
                                if (onOrganizationScopeChange && val) {
                                    onOrganizationScopeChange({ host: scmHost, name: val });
                                } else if (onOrganizationScopeChange && !val) {
                                    onOrganizationScopeChange(undefined);
                                }
                            }}
                            onOpenChange={handleOpenChange}
                            loading={false}
                            className="flex-1"
                            disabled={disabled}
                        >
                            <Combobox.Value>
                                {(val) => (
                                    <Combobox.ValueLabel className="max-w-40 truncate">
                                        {val || comboboxPlaceholder}
                                    </Combobox.ValueLabel>
                                )}
                            </Combobox.Value>
                            <Combobox.Popover sameWidth={false} className="min-w-[--popover-anchor-width] max-w-96">
                                <Combobox.SearchBox loading={isFetchingOrgs} />
                                {organizations && organizations.length > 0 ? (
                                    <Combobox.List
                                        items={organizations}
                                        searchKeys={["name"]}
                                        noMatchesComponent={<Combobox.Empty>No organizations found</Combobox.Empty>}
                                    >
                                        {(org) => (
                                            <Combobox.ListItem key={org.name} value={org.name} title={org.name} />
                                        )}
                                    </Combobox.List>
                                ) : (
                                    <Combobox.Empty>No organizations found</Combobox.Empty>
                                )}
                            </Combobox.Popover>
                        </Combobox>
                    )}
                    <Button
                        variant="ghost"
                        onClick={handleOpenSettings}
                        disabled={!settingsUrl || disabled}
                        data-testid="open-webhook-settings-org"
                        data-tracking-id="open-webhook-settings-org"
                        className="gap-0 px-3 text-sm"
                    >
                        Open&nbsp;{providerName}&nbsp;webhook settings
                    </Button>
                </div>
            </div>
        );
    }

    // Repository type: multi-scope selection
    return (
        <div className="flex flex-col gap-2">
            <Label htmlFor="webhook-scope" className="text-sm font-medium">
                Repositories
            </Label>

            {/* Selected scopes list */}
            {scopes.length > 0 && (
                <div className="flex flex-col gap-1" data-testid="webhook-scopes-list">
                    {scopes.map((scope, index) => {
                        const repoSettingsUrl = getWebhookSettingsUrl(
                            provider,
                            webhookType,
                            scopeToDisplayString(scope),
                            isNewWebhook,
                        );
                        return (
                            <div key={`${scope.owner}/${scope.name}`} className="flex items-center gap-1">
                                <div className="flex flex-1 items-center gap-2 rounded-md border border-pk-border bg-pk-surface-secondary px-3 py-1.5 text-sm">
                                    <span className="flex-1 truncate">{scopeToDisplayString(scope)}</span>
                                    {!readOnly && !disabled && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveScopeClick(index)}
                                            className="text-pk-content-tertiary hover:text-pk-content-primary"
                                            aria-label={`Remove ${scopeToDisplayString(scope)}`}
                                            data-testid={`remove-scope-${index}`}
                                            data-tracking-id={`remove-webhook-scope-${index}`}
                                        >
                                            <IconClose size="sm" />
                                        </button>
                                    )}
                                </div>
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        if (repoSettingsUrl) {
                                            window.open(repoSettingsUrl, "_blank", "noopener,noreferrer");
                                        }
                                    }}
                                    disabled={!repoSettingsUrl || disabled}
                                    data-testid={`open-webhook-settings-${index}`}
                                    data-tracking-id={`open-webhook-settings-${index}`}
                                    className="gap-0 px-3 text-sm"
                                >
                                    Open&nbsp;{providerName}&nbsp;webhook settings
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add repository combobox */}
            {!readOnly && (
                <Combobox
                    value=""
                    onValueChange={handleRepoSelected}
                    onOpenChange={handleOpenChange}
                    loading={false}
                    disabled={disabled}
                >
                    <Combobox.Value>
                        {() => (
                            <Combobox.ValueLabel className="max-w-40 truncate">
                                {comboboxPlaceholder}
                            </Combobox.ValueLabel>
                        )}
                    </Combobox.Value>
                    <Combobox.Popover sameWidth={false} className="max-w-96">
                        <Combobox.SearchBox onValueChanged={handleSearchValueChanged} loading={isLoadingRepos} />
                        {availableRepositories && availableRepositories.length > 0 ? (
                            <Combobox.List
                                items={availableRepositories}
                                searchKeys={[]}
                                disableFiltering
                                noMatchesComponent={<Combobox.Empty>No repositories found</Combobox.Empty>}
                            >
                                {(repo) => (
                                    <Combobox.ListItem
                                        key={repo.url}
                                        value={extractRepoPath(repo.url)}
                                        title={extractRepoPath(repo.url)}
                                    />
                                )}
                            </Combobox.List>
                        ) : (
                            <Combobox.Empty>No repositories found</Combobox.Empty>
                        )}
                    </Combobox.Popover>
                </Combobox>
            )}

            {/* Confirmation dialog for removing a repository scope */}
            {pendingRemoveIndex !== null && scopes[pendingRemoveIndex] && (
                <RemoveScopeConfirmDialog
                    scope={scopes[pendingRemoveIndex]}
                    provider={provider}
                    webhookType={webhookType}
                    providerName={providerName}
                    isNewWebhook={isNewWebhook}
                    onConfirm={handleConfirmRemoveScope}
                    onCancel={handleCancelRemoveScope}
                />
            )}
        </div>
    );
};

type RemoveScopeConfirmDialogProps = {
    scope: WebhookScopeEntry;
    provider: WebhookProvider;
    webhookType: WebhookType;
    providerName: string;
    isNewWebhook: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const RemoveScopeConfirmDialog: FC<RemoveScopeConfirmDialogProps> = ({
    scope,
    provider,
    webhookType,
    providerName,
    isNewWebhook,
    onConfirm,
    onCancel,
}) => {
    const display = scopeToDisplayString(scope);
    const settingsUrl = getWebhookSettingsUrl(provider, webhookType, display, isNewWebhook);

    return (
        <Dialog open onOpenChange={(open) => !open && onCancel()}>
            <DialogContent data-testid="remove-scope-confirm-dialog" data-track-location={TrackLocations.RemoveWebhookScopeDialog}>
                <DialogHeader>
                    <DialogTitle>Remove repository</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to remove <strong>{display}</strong> from this webhook?
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <div className="rounded-lg border border-border-base px-4 py-3 text-content-primary">
                        <div className="flex gap-3">
                            <IconTriangleExclamation size={16} className="mt-0.5 shrink-0 text-content-warning" />
                            <div className="flex flex-col gap-2">
                                <p>
                                    Remember to also remove the webhook from your {providerName} repository settings to
                                    stop sending events.
                                </p>
                                {settingsUrl && (
                                    <ExternalLink
                                        href={settingsUrl}
                                        iconSize="sm"
                                        data-testid="remove-scope-scm-settings-link"
                                        data-tracking-id="open-scm-settings-remove-scope-dialog"
                                    >
                                        {display}
                                    </ExternalLink>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="cancel-remove-scope-button">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        data-testid="confirm-remove-scope-button"
                        data-tracking-id="confirm-remove-webhook-scope"
                    >
                        Remove
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
